<script module lang="ts">
  import Layout from '../components/Layout.svelte'
  import { translate } from '../i18n'
  import type { Category } from '../categories'
  import type { Locale } from '../translations'

  export const layout = (props: { category: Category; locale: Locale }) => ({
    component: Layout,
    props: { title: translate(props.locale, `categories.${props.category}.heading`) },
  })
</script>

<script lang="ts">
  import { untrack } from 'svelte'
  import { router } from '@inertiajs/svelte'
  import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons'
  import type { SerializedPage, SerializedPagination } from '../types'
  import { CATEGORIES } from '../categories'
  import PageCard from '../components/PageCard.svelte'
  import Pagination from '../components/Pagination.svelte'
  import Input from '../components/Input.svelte'
  import Icon from '../components/Icon.svelte'
  import SectionHeading from '../components/SectionHeading.svelte'
  import { getI18n } from '../i18n'

  let { pages, pagination, category, query = '' }: {
    pages: SerializedPage[]
    pagination: SerializedPagination
    category: Category
    query?: string
  } = $props()

  const i18n = getI18n()
  const categoryPath = $derived(i18n.path(`/pages/${category}`))

  let inputValue = $state(untrack(() => query ?? ''))

  let searchTimeout: ReturnType<typeof setTimeout> | undefined

  // Only typing searches: the query of the URL is already rendered.
  function search(value: string) {
    clearTimeout(searchTimeout)
    if (value === (query ?? '')) return

    searchTimeout = setTimeout(() => {
      router.get(
        categoryPath,
        value ? { query: value } : {},
        { preserveState: true, preserveScroll: true, replace: true }
      )
    }, 300)
  }

  $effect(() => () => clearTimeout(searchTimeout))

  function pageHref(pageNumber: number): string {
    const parameters = new URLSearchParams()
    if (query) parameters.set('query', query)
    if (pageNumber > 1) parameters.set('page', String(pageNumber))
    const search = parameters.toString()
    return search ? `${categoryPath}?${search}` : categoryPath
  }

  function clearSearch() {
    inputValue = ''
    search('')
  }
</script>

<SectionHeading eyebrow={i18n.t(`categories.${category}.name`)} variant={CATEGORIES[category].variant}>
  {i18n.t(`categories.${category}.heading`)}
</SectionHeading>
<p class="lead">{i18n.t(`categories.${category}.lead`)}</p>

<div class="search-wrapper">
  <form class="search" onsubmit={event => event.preventDefault()}>
    <Input type="search" name="search[query]" bind:value={inputValue} oninput={(event) => search(event.currentTarget.value)} placeholder={i18n.t('search.placeholder')} aria-label={i18n.t('search.label')} autocomplete="off">
      {#snippet start()}
        <span class="search-icon"><Icon icon={faMagnifyingGlass} /></span>
      {/snippet}
      {#snippet end()}
        {#if inputValue}
          <button type="button" class="search-clear" onclick={clearSearch} aria-label={i18n.t('search.clear')}>
            <Icon icon={faXmark} />
          </button>
        {/if}
      {/snippet}
    </Input>
  </form>
  <!-- On one line: whitespace would keep the paragraph from matching :empty. -->
  <p class="results-count" aria-live="polite">{#if query && pages.length > 0}{i18n.t('search.results', { count: pagination.totalCount, query })}{:else if query}<span class="results-count-empty">{i18n.t('search.emptyTitle')}</span>{/if}</p>
</div>

<div id="results" class="results">
  {#if pages.length > 0}
    <div class="results-grid">
      {#each pages as page, index (page.id)}
        <PageCard {page} {category} headingLevel="h2" priority={index === 0} />
      {/each}
    </div>
    <Pagination {pagination} {pageHref} />
  {:else}
    <div class="results-empty">
      <svg class="results-empty-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
      {#if query}
        <strong class="results-empty-title">{i18n.t('search.emptyTitle')}</strong>
        <p class="results-empty-description">{i18n.t('search.emptyDescription')}</p>
      {:else}
        <strong class="results-empty-title">{i18n.t('categories.emptyTitle')}</strong>
        <p class="results-empty-description">{i18n.t('categories.emptyDescription')}</p>
      {/if}
    </div>
  {/if}
</div>

<style>
  .lead {
    color: var(--color-text-muted);
    max-width: 640px;
  }

  .search-wrapper {
    margin-block: var(--space-9);
  }

  .search-icon {
    display: flex;
    flex: none;
    font-size: 18px;
    color: var(--color-text-muted);
  }

  .search-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    border: none;
    background-color: transparent;
    color: var(--color-text-muted);
    width: 24px;
    height: 24px;
    border-radius: var(--radius-6);
    cursor: pointer;
    padding: 0;
    font-size: var(--font-size-3);
    transition: color var(--transition-fast);

    &:hover {
      color: var(--color-text);
    }
  }

  .results-count {
    font-size: var(--font-size-4);
    color: var(--color-text-muted);
    margin: var(--space-4) 0 0;

    &:empty {
      margin: 0;
    }
  }

  /* The empty state below shows this visually; screen readers hear it from the live region. */
  .results-count-empty {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .results-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-9);

    @media (--mobile) {
      grid-template-columns: 1fr;
    }
  }

  .results-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--space-5);
    padding: var(--space-14) var(--space-7);
    color: var(--color-text-muted);
    border: var(--border-2) dashed var(--color-border-1);
    border-radius: var(--radius-4);
  }

  .results-empty-icon {
    width: 32px;
    height: 32px;
    opacity: var(--opacity-2);
  }

  .results-empty-title {
    color: var(--color-text);
    font-size: var(--font-size-5);
  }

  .results-empty-description {
    margin: 0;
    max-width: 40ch;
  }
</style>
